import { Link, Outlet } from "react-router-dom";
import { Clock, Home, MapPin, Package, Phone, Shirt, ShoppingBag, ShoppingCart, Tags } from "lucide-react";
import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import PublicTopNav from "../../components/PublicTopNav";
import { useShopperAuth } from "../../context/ShopperAuthContext";
import { resolveMediaUrl } from "../../lib/api";
import { usePublicPageBranding } from "../../hooks/usePublicPageBranding";
import { storePath, storePublicUrl } from "../../lib/storeApi";
import StoreSearchBox from "./StoreSearchBox";

const VirtualTryOn = lazy(() => import("../SignagePlayer/VirtualTryOn"));

function getTryOnBrowserId() {
  const key = "rp_try_on_browser_id";
  try {
    let value = localStorage.getItem(key);
    if (!value) {
      value = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `browser-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(key, value);
    }
    return value;
  } catch {
    return `browser-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

export default function StoreLayout({ storeName, storeLogoUrl, clientRef, resolvedHost }) {
  const { shopper, logout, api } = useShopperAuth();
  const [cartCount, setCartCount] = useState(0);
  const [announcement, setAnnouncement] = useState("");
  const [storeMeta, setStoreMeta] = useState({ vertical: "", categories: [], whatsapp: "", phone: "", address: "", hours: "", mapUrl: "", organizationName: "", orderingEnabled: true, tryOnEnabled: false, tryOnPhysicalEnabled: false });
  const [tryOnOpen, setTryOnOpen] = useState(false);
  const [tryOnProducts, setTryOnProducts] = useState([]);
  const tryOnHeaders = useMemo(() => ({ "X-Try-On-Client": getTryOnBrowserId() }), []);

  usePublicPageBranding(storeName, storeLogoUrl);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("");
        setAnnouncement(data?.profile?.announcement || "");
        setStoreMeta({
          vertical: data?.vertical || "",
          categories: data?.categories || [],
          whatsapp: data?.profile?.whatsapp_number || data?.phone || "",
          phone: data?.phone || "",
          address: data?.seo_settings?.business_address || data?.address || "",
          hours: data?.profile?.hours || "",
          mapUrl: data?.seo_settings?.google_map_url || "",
          organizationName: data?.organization_name || data?.name || "Restaurant",
          orderingEnabled: data?.ordering_enabled !== false,
          tryOnEnabled: data?.try_on?.enabled === true,
          tryOnPhysicalEnabled: data?.try_on?.physical_enabled === true,
        });
        if (data?.try_on?.enabled === true && data?.vertical === "retail_store") {
          try {
            const productResponse = await api.get("/products", { params: { limit: 500, sort: "newest" } });
            setTryOnProducts(productResponse.data?.items || []);
          } catch { setTryOnProducts([]); }
        }
      } catch {
        setAnnouncement("");
        setStoreMeta({ vertical: "", categories: [], whatsapp: "", phone: "", address: "", hours: "", mapUrl: "", organizationName: "", orderingEnabled: true, tryOnEnabled: false, tryOnPhysicalEnabled: false });
      }
    })();
  }, [api]);

  useEffect(() => {
    if (!shopper || shopper === false) {
      setCartCount(0);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/cart");
        setCartCount(data.item_count || 0);
      } catch {
        setCartCount(0);
      }
    })();
  }, [shopper, api]);

  const homePath = storePath(clientRef, "", resolvedHost);
  const cartPath = storePath(clientRef, "cart", resolvedHost);
  const ordersPath = storePath(clientRef, "orders", resolvedHost);
  const profilePath = storePath(clientRef, "profile", resolvedHost);
  const loginPath = storePath(clientRef, "login", resolvedHost);
  const isRetailStore = storeMeta.vertical === "retail_store";
  const isReadOnlyRestaurant = storeMeta.vertical === "retailer" && storeMeta.orderingEnabled === false;
  const searchPath = storePath(clientRef, "search", resolvedHost);
  const contactPath = storePath(clientRef, "contact", resolvedHost);
  const aboutPath = storePath(clientRef, "about", resolvedHost);
  const whatsappHref = storeMeta.whatsapp ? `https://wa.me/${String(storeMeta.whatsapp).replace(/\D/g, "").replace(/^0+/, "")}` : "";
  const navIconClass = isRetailStore
    ? "flex flex-col items-center gap-0.5 min-w-[44px] px-1 py-1 rounded-md hover:bg-[#F3F4F6] text-[#374151]"
    : "flex flex-col items-center gap-0.5 min-w-[44px] px-1 py-1 rounded-md hover:bg-white/10 hover:text-[#FF9900] text-white";
  const cartButtonClass = isRetailStore
    ? "flex items-center gap-2 bg-[#111827] hover:bg-[#374151] text-white px-3 py-2 rounded-md ml-1"
    : "flex items-center gap-2 bg-[#232F3E] hover:bg-[#37475A] px-3 py-2 rounded-md ml-1";

  return (
    <div className={["min-h-screen bg-[#F5F7FA] text-[#111827] flex flex-col", isRetailStore ? "pb-16 md:pb-0" : "pb-0"].join(" ")}>
      {tryOnOpen ? <Suspense fallback={<div className="fixed inset-0 z-[99] flex items-center justify-center bg-black text-white">Loading Virtual Try-On…</div>}>
        <VirtualTryOn open onClose={() => setTryOnOpen(false)} endpointBase={`${api.defaults.baseURL}/try-on`} requestHeaders={tryOnHeaders} products={tryOnProducts} physicalEnabled={storeMeta.tryOnPhysicalEnabled} />
      </Suspense> : null}
      <PublicTopNav
        variant={isRetailStore ? "light" : "dark"}
        title={storeName || "Store"}
        subtitle={isRetailStore ? "Retail store" : isReadOnlyRestaurant ? "Menu" : "Online shop"}
        homeHref={homePath}
        siteUrl={storePublicUrl(clientRef, resolvedHost)}
        siteName={storeName || "Store"}
        user={isReadOnlyRestaurant ? null : shopper}
        profileHref={!isReadOnlyRestaurant && shopper && shopper !== false ? profilePath : ""}
        loginHref={!isReadOnlyRestaurant && (!shopper || shopper === false) ? loginPath : ""}
        onLogout={logout}
        brandIcon={storeLogoUrl ? (
          <img src={resolveMediaUrl(storeLogoUrl)} alt="" className="w-9 h-9 rounded object-cover shrink-0 bg-white" />
        ) : (
          <div className="w-9 h-9 bg-[#FF9900] rounded flex items-center justify-center shrink-0">
            <ShoppingBag className="w-5 h-5 text-[#131921]" />
          </div>
        )}
        trailing={isReadOnlyRestaurant ? null : (
          <>
            {shopper && shopper !== false ? (
              <Link to={ordersPath} className={navIconClass}>
                <Package className="w-5 h-5" />
                <span className="text-[10px] font-semibold">Orders</span>
              </Link>
            ) : null}
            <Link to={cartPath} className={cartButtonClass}>
              <ShoppingCart className="w-5 h-5" />
              <span className="font-bold text-sm">{cartCount}</span>
            </Link>
          </>
        )}
      >
        <StoreSearchBox />
      </PublicTopNav>
      {announcement && (
        <div className={`${isRetailStore ? "bg-[#111827] text-white" : "bg-[#FF9900] text-[#111827]"} text-sm font-medium text-center py-2 px-4`}>
          {announcement}
        </div>
      )}
      {isRetailStore && storeMeta.categories?.length > 0 && (
        <div className="bg-white border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-4 py-2 flex gap-2 overflow-x-auto">
            <Link to={searchPath} className="inline-flex min-h-10 items-center gap-1.5 rounded-full bg-[#111827] px-4 text-xs font-semibold text-white shrink-0">
              <Tags className="w-3.5 h-3.5" /> All categories
            </Link>
            {storeMeta.categories.slice(0, 12).map((cat) => (
              <Link key={cat.id} to={storePath(clientRef, `category/${cat.slug || cat.seo_slug || cat.id}`, resolvedHost)} className="inline-flex min-h-10 items-center rounded-full border border-[#E5E7EB] bg-white px-4 text-xs font-semibold text-[#374151] hover:border-[#111827] shrink-0">
                {cat.name}
              </Link>
            ))}
          </div>
        </div>
      )}
      <main className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-4 sm:py-6">
        <Outlet context={{ setCartCount, orderingEnabled: !isReadOnlyRestaurant }} />
      </main>
      {!isRetailStore && (
        <footer className="w-full bg-[#131921] px-4 py-4 text-sm text-white">
          <div className="mx-auto flex w-full max-w-7xl items-center justify-between gap-4">
            <div className="min-w-0 truncate font-bold">{storeMeta.organizationName || storeName || "Restaurant"}</div>
            <div className="flex shrink-0 items-center gap-4">
              <Link to={aboutPath} className="font-semibold text-white hover:text-[#FF9900]">About Us</Link>
              {storeMeta.phone && <a href={"tel:" + String(storeMeta.phone).replace(/\s+/g, "")} className="inline-flex items-center gap-1.5 font-semibold text-white hover:text-[#FF9900]"><Phone className="h-4 w-4" />Call</a>}
              {storeMeta.whatsapp && <a href={whatsappHref} target="_blank" rel="noreferrer" className="font-semibold text-emerald-300">WhatsApp</a>}
            </div>
          </div>
          {(storeMeta.address || storeMeta.hours || storeMeta.mapUrl) && (
            <div className="mx-auto mt-2 flex w-full max-w-7xl flex-wrap gap-x-4 gap-y-1 border-t border-white/10 pt-2 text-xs text-white/65">
              {storeMeta.address && <span className="inline-flex items-start gap-1.5"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />{storeMeta.address}</span>}
              {storeMeta.hours && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" />{storeMeta.hours}</span>}
              {storeMeta.mapUrl && <a href={storeMeta.mapUrl} target="_blank" rel="noreferrer" className="font-semibold text-[#FFB84D] hover:underline">Directions</a>}
            </div>
          )}
        </footer>
      )}      {isRetailStore && storeMeta.tryOnEnabled && !tryOnOpen ? <button type="button" onClick={() => setTryOnOpen(true)} className="fixed bottom-20 right-4 z-40 inline-flex items-center gap-2 rounded-full bg-violet-600 px-5 py-3 font-bold text-white shadow-xl hover:bg-violet-500 md:bottom-6"><Shirt className="h-5 w-5" />Try it on</button> : null}
      {isRetailStore && (
        <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-4 border-t border-[#E5E7EB] bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur md:hidden">
          <Link to={homePath} className="flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-[#374151]"><Home className="w-4 h-4" />Home</Link>
          <Link to={searchPath} className="flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-[#374151]"><Tags className="w-4 h-4" />Shop</Link>
          <Link to={cartPath} className="flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-[#374151]"><ShoppingCart className="w-4 h-4" />Cart {cartCount ? `(${cartCount})` : ""}</Link>
          <a href={whatsappHref || contactPath} className="flex min-h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-[#374151]"><Phone className="w-4 h-4" />Contact</a>
        </nav>
      )}
    </div>
  );
}
